// src/components/PendingCallbacksPanel.js
// 待回撥訂位 tab（feature_plans/115 Phase C）— 掛在「訂單管理」console 第二個 tab
//
// 這些單是 AI 在電話裡收下、但超過線上訂位人數上限而需要老闆回電確認的。
// 在這個分頁出現之前，它們只存在通話紀錄的逐字稿裡，prod 實測有一半從來沒人處理。
//
// 版型比照本頁既有慣例：手機卡片（renderMobileCards）／桌機表格。
// 資料自管；counts 回拋給父層畫 tab badge（比照 TakeoutOrdersPanel）。
// 點一列 → onSelect(row)，由父層把既有訂位表單預先填好——
// 自選桌號／預定料理／特殊需求全部沿用，這裡不重造表單。
// ============================================================

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Paper, Typography, IconButton, Skeleton, Tooltip, Drawer, CircularProgress,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import PhoneCallbackIcon from '@mui/icons-material/PhoneCallback';
import ChatIcon from '@mui/icons-material/Chat';

import bookingRequestService from '../services/bookingRequestService';
import callLogService from '../services/callLogService';
import CallTranscriptView from './conversation/CallTranscriptView';

const BLUE = '#145da0';
const AMBER = '#ff9800';
const AMBER_BG = '#fff3e0';
const AMBER_FG = '#e65100';

const fmtTime = (t) => String(t || '').slice(0, 5);

const fmtDate = (ymd) => {
  if (!ymd) return '—';
  const [, m, d] = String(ymd).split('-');
  return `${parseInt(m, 10)}/${parseInt(d, 10)}`;
};

const GENDER_LABEL = { mr: '先生', ms: '小姐' };
const displayName = (name, gender) => {
  if (!name) return '—';
  return `${name}${GENDER_LABEL[gender] || ''}`;
};

const SOURCE_LABEL = { voice: 'AI 電話', web: '線上', line: 'LINE', manual: '手動' };

const STATUS_CHIP = {
  pending: { label: '待回撥', bg: AMBER_BG, fg: AMBER_FG },
  no_answer: { label: '聯絡不上', bg: '#fef3c7', fg: '#92400e' },
};

// 字級一律沿用父頁的縮放（全域「A」控制）——這一頁是手動 fz 縮放、不吃 wrapper zoom，
// 寫死 px 會讓這個分頁看起來比今日訂位小一號，而且調「A」也不會跟著動。
const PendingCallbacksPanel = ({
  refreshSignal = 0,
  onCountsChange,
  onSelect,
  selectedId = null,
  isMobile = false,
  mobileFs = 20,
  headerSx: headerSxProp,
  cellSx: cellSxProp,
  fz = (base) => base,
  showSnack,
}) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  // 逐字稿抽屜 —— 重用通話紀錄頁的同一個元件（CallTranscriptView），
  // 老闆在兩個地方看到的對話長得一模一樣，連客人錄音播放都一樣。
  // RestaurantReservationsPage 當初把這個抽屜 stub 掉，理由是
  // 「網頁/現場訂位沒有 conversationId」——待回撥每一筆都有 callLogId，不受此限。
  const [transcriptOpen, setTranscriptOpen] = useState(false);
  const [transcriptLog, setTranscriptLog] = useState(null);
  const [transcriptLoading, setTranscriptLoading] = useState(false);

  const openTranscript = useCallback(async (callLogId) => {
    setTranscriptOpen(true);
    setTranscriptLog(null);
    setTranscriptLoading(true);
    try {
      const r = await callLogService.getCallLogById(callLogId);
      if (r?.success) setTranscriptLog(r.data);
      else if (showSnack) showSnack(r?.message || '讀取對話紀錄失敗', 'error');
    } catch (e) {
      if (showSnack) showSnack('讀取對話紀錄失敗', 'error');
    } finally {
      setTranscriptLoading(false);
    }
  }, [showSnack]);

  // 比照 TakeoutOrdersPanel：用 ref 拿最新的 callback，避免它進 fetch 的相依陣列造成重抓迴圈
  const onCountsRef = useRef(onCountsChange);
  onCountsRef.current = onCountsChange;

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const body = await bookingRequestService.list({ status: 'open' });
      const data = body?.data || [];
      setRows(data);
      if (onCountsRef.current) onCountsRef.current(body?.openCount ?? data.length);
    } catch (error) {
      console.error('讀取待回撥訂位失敗:', error);
      if (showSnack) showSnack('讀取待回撥訂位失敗', 'error');
    } finally {
      setLoading(false);
    }
  }, [showSnack]);

  useEffect(() => { fetchRows(); }, [refreshSignal, fetchRows]);

  // ── 空狀態 ────────────────────────────────────────────────
  const renderEmpty = () => (
    <Box sx={{ py: 6, textAlign: 'center', color: '#999' }}>
      <PhoneCallbackIcon sx={{ fontSize: 48, color: '#ddd', mb: 1, display: 'block', mx: 'auto' }} />
      <Typography sx={{ fontSize: fz(14) }}>✅ 沒有待回撥的訂位</Typography>
    </Box>
  );

  // ── 手機：卡片（左側琥珀色 4px 條，比照候位卡片）────────────
  const renderCards = () => {
    if (loading && rows.length === 0) {
      return Array.from({ length: 4 }).map((_, i) => (
        <Skeleton key={`sk-${i}`} variant="rounded" height={92} sx={{ borderRadius: '14px', mb: 1 }} />
      ));
    }
    if (!loading && rows.length === 0) return renderEmpty();

    const fs = mobileFs;
    return rows.map((r, index) => (
      <Paper
        key={r.id}
        onClick={() => onSelect && onSelect(r)}
        sx={{
          display: 'flex', gap: 1.5, alignItems: 'stretch', p: 1.75, mb: 1, borderRadius: '14px',
          boxShadow: '0 1px 3px rgba(15,43,74,0.08)', cursor: 'pointer',
          borderLeft: `4px solid ${AMBER}`,
          outline: selectedId === r.id ? `2px solid ${BLUE}` : 'none',
        }}
      >
        {/* col-l：訂位日期 + 時間 */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.75, minWidth: 64, flexShrink: 0 }}>
          <Typography sx={{ fontSize: fs, fontWeight: 800, color: '#0f2b4a', letterSpacing: '.5px' }}>
            {fmtDate(r.reservationDate)}
          </Typography>
          <Typography sx={{ fontSize: fs - 4, fontWeight: 700, color: '#334155' }}>
            {fmtTime(r.reservationTime)}
          </Typography>
          <Box sx={{
            fontSize: fs - 8, fontWeight: 800, px: 1, py: '2px', borderRadius: '8px', whiteSpace: 'nowrap',
            bgcolor: index === 0 ? AMBER_BG : '#eceff1', color: index === 0 ? AMBER_FG : '#546e7a',
          }}>
            {index === 0 ? `最近 · ${r.dayLabel}` : r.dayLabel}
          </Box>
        </Box>

        {/* col-m：姓名 + 電話 + 原因 */}
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.6 }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
            <Typography sx={{ fontSize: fs, fontWeight: 700 }}>
              {displayName(r.customerName, r.customerGender)}
            </Typography>
            <Typography component="span" sx={{ fontSize: fs - 2, fontWeight: 600, color: '#334155' }}>
              {r.customerPhone}
            </Typography>
          </Box>
          <Typography sx={{ fontSize: fs - 6, color: AMBER_FG, fontWeight: 600 }}>{r.reasonText}</Typography>
          {SOURCE_LABEL[r.source] && (
            <Box component="span" sx={{
              alignSelf: 'flex-start', fontSize: fs - 8, fontWeight: 700, px: 0.9, py: '2px',
              borderRadius: '5px', bgcolor: '#eceff1', color: '#546e7a',
            }}>
              {SOURCE_LABEL[r.source]}
            </Box>
          )}
        </Box>

        {/* col-r：人數 + 狀態 */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', justifyContent: 'space-between', gap: 1, flexShrink: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '3px' }}>
            <Typography sx={{ fontSize: fs + 4, fontWeight: 800, color: '#0f2b4a', lineHeight: 1 }}>{r.adults || 0}</Typography>
            <Typography sx={{ fontSize: fs - 6, color: '#6b7280', fontWeight: 600 }}>大</Typography>
            {r.children > 0 && (<>
              <Typography sx={{ fontSize: fs + 4, fontWeight: 800, color: '#0f2b4a', lineHeight: 1 }}>{r.children}</Typography>
              <Typography sx={{ fontSize: fs - 6, color: '#6b7280', fontWeight: 600 }}>小</Typography>
            </>)}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {r.callLog && (
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); openTranscript(r.callLog.id); }}
                sx={{ color: '#546e7a' }}
              >
                <ChatIcon sx={{ fontSize: fs - 2 }} />
              </IconButton>
            )}
            {STATUS_CHIP[r.status] && (
              <Box sx={{
                fontSize: fs - 8, fontWeight: 700, px: 1, py: '2px', borderRadius: '8px', whiteSpace: 'nowrap',
                bgcolor: STATUS_CHIP[r.status].bg, color: STATUS_CHIP[r.status].fg,
              }}>
                {STATUS_CHIP[r.status].label}
              </Box>
            )}
          </Box>
        </Box>
      </Paper>
    ));
  };

  // ── 桌機：表格（表頭與 cell 沿用今日訂位那張表的同一組 sx）──
  const headerSx = headerSxProp
    || { bgcolor: '#ddd', fontWeight: 'bold', fontSize: '14px', position: 'sticky', top: 0, zIndex: 2, borderBottom: '1px solid #ccc' };
  const cellSx = cellSxProp || { py: 1.75, fontSize: 16 };
  const chipFz = (cellSx.fontSize || 16) - 3;

  const renderTable = () => (
    <TableContainer component={Paper} sx={{ mt: 2, boxShadow: '0 2px 4px rgba(0,0,0,0.06)', maxHeight: 'calc(100vh - 260px)', overflow: 'auto' }}>
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={headerSx}>訂位日期</TableCell>
            <TableCell sx={headerSx}>時間</TableCell>
            <TableCell sx={headerSx}>姓名</TableCell>
            <TableCell sx={headerSx} align="right">人數</TableCell>
            <TableCell sx={headerSx}>電話</TableCell>
            <TableCell sx={headerSx}>原因</TableCell>
            <TableCell sx={headerSx}>來電</TableCell>
            <TableCell sx={headerSx}>狀態</TableCell>
            <TableCell sx={headerSx} align="center">對話</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading && rows.length === 0 && Array.from({ length: 5 }).map((_, i) => (
            <TableRow key={`sk-${i}`}>
              {Array.from({ length: 9 }).map((__, j) => (
                <TableCell key={j} sx={cellSx}><Skeleton variant="text" /></TableCell>
              ))}
            </TableRow>
          ))}
          {!loading && rows.length === 0 && (
            <TableRow><TableCell colSpan={9} sx={{ border: 0 }}>{renderEmpty()}</TableCell></TableRow>
          )}
          {rows.map((r, index) => (
            <TableRow
              key={r.id}
              hover
              onClick={() => onSelect && onSelect(r)}
              sx={{
                cursor: 'pointer',
                bgcolor: selectedId === r.id ? '#e8f0f9' : undefined,
                borderLeft: `4px solid ${AMBER}`,
              }}
            >
              <TableCell sx={{ ...cellSx, fontWeight: 700, color: '#0f2b4a' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {fmtDate(r.reservationDate)}
                  <Box component="span" sx={{
                    fontSize: chipFz, fontWeight: 700, px: 1, py: '1px', borderRadius: '8px',
                    bgcolor: index === 0 ? AMBER_BG : '#eceff1', color: index === 0 ? AMBER_FG : '#546e7a',
                  }}>
                    {index === 0 ? `最近 · ${r.dayLabel}` : r.dayLabel}
                  </Box>
                </Box>
              </TableCell>
              <TableCell sx={cellSx}>{fmtTime(r.reservationTime)}</TableCell>
              <TableCell sx={{ ...cellSx, fontWeight: 600 }}>{displayName(r.customerName, r.customerGender)}</TableCell>
              <TableCell sx={{ ...cellSx, fontWeight: 700 }} align="right">
                {r.adults || 0}{r.children > 0 ? ` + ${r.children}` : ''}
              </TableCell>
              <TableCell sx={{ ...cellSx, fontWeight: 600 }}>{r.customerPhone}</TableCell>
              <TableCell sx={{ ...cellSx, color: AMBER_FG, fontWeight: 600 }}>{r.reasonText}</TableCell>
              <TableCell sx={cellSx}>{SOURCE_LABEL[r.source] || r.source}</TableCell>
              <TableCell sx={cellSx}>
                {STATUS_CHIP[r.status] && (
                  <Box component="span" sx={{
                    fontSize: chipFz, fontWeight: 700, px: 1, py: '2px', borderRadius: '8px',
                    bgcolor: STATUS_CHIP[r.status].bg, color: STATUS_CHIP[r.status].fg,
                  }}>
                    {STATUS_CHIP[r.status].label}
                  </Box>
                )}
              </TableCell>
              <TableCell sx={cellSx} align="center">
                {r.callLog ? (
                  <Tooltip title="查看對話紀錄">
                    <IconButton
                      size="small"
                      onClick={(e) => { e.stopPropagation(); openTranscript(r.callLog.id); }}
                      sx={{ color: '#546e7a' }}
                    >
                      <ChatIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                ) : (
                  <Typography sx={{ fontSize: chipFz, color: '#cbd5e1' }}>—</Typography>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Box sx={{ flex: 1, overflow: 'auto', px: { xs: 1.5, md: 3 }, pb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pt: 1.5 }}>
        <Typography sx={{ fontSize: fz(13), color: '#64748b' }}>
          AI 在電話裡收下、需要回電確認的訂位。點一筆即可帶入訂位表單。
        </Typography>
        <Tooltip title="重新整理">
          <IconButton size="small" onClick={fetchRows}><RefreshIcon fontSize="small" /></IconButton>
        </Tooltip>
      </Box>
      {isMobile ? renderCards() : renderTable()}

      {/* 逐字稿抽屜 —— anchor=right / 440px，與通話紀錄頁同一個元件、同樣式、同音檔播放。
          不傳 onBack：這裡沒有「詳情」上一層可回（元件註解明訂：訂位抽屜不提供）。 */}
      <Drawer
        anchor="right"
        open={transcriptOpen}
        onClose={() => setTranscriptOpen(false)}
        PaperProps={{ sx: { width: { xs: '100%', sm: 440 }, overflow: 'hidden' } }}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          {transcriptLoading && (
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CircularProgress size={28} />
            </Box>
          )}
          {!transcriptLoading && transcriptLog && (
            <CallTranscriptView
              log={transcriptLog}
              fontSize={fz(14)}
              onClose={() => setTranscriptOpen(false)}
            />
          )}
        </Box>
      </Drawer>
    </Box>
  );
};

export default PendingCallbacksPanel;
